import { Box, Typography } from "@mui/material";
import { GridAddIcon, GridCallbackDetails, GridColumnOrderChangeParams, MuiEvent } from "@mui/x-data-grid";
import React, { ReactElement, useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import DeleteIcon from "../../../assets/images/icons/icon_delete.svg";
import ExportIcon from "../../../assets/images/icons/icon_export.svg";
import GreenEllipse from "../../../assets/images/icons/icon_green_ellipse.svg";
import RedEllipse from "../../../assets/images/icons/icon_red_ellipse.svg";
import { DataWrapperBox } from "../../../components/Boxes/DataWrapperBox/DataWrapperBox.style";
import HorizontalBox from "../../../components/Boxes/HorizontalBox/HorizontalBox";
import Button from "../../../components/Buttons/Button/Button";
import SplitButton from "../../../components/Buttons/SplitButton/SplitButton";
import DataGrid from "../../../components/DataGrid/DataGrid";
import DeleteConfirmationDialog from "../../../components/Modal/DeleteConfirmationDialog/DeleteConfirmationDialog";
import FullScreenModal from "../../../components/Modal/FullScreenModal/FullScreenModal";
import Progress from "../../../components/Progress/ProgressModal/ProgressModal";
import SearchBar from "../../../components/SearchBarWithDebounce/SearchBar";
import { AppContext } from "../../../core/context/appContextProvider";
import RoutingPaths from "../../../core/routing/routingPaths";
import useRole from "../../../core/routing/useRole";
import { saveColumnOrder } from "../../../services/columnOrder.service";
import { deleteInvestors, updateInvestorStatus } from "../../../services/investor.service";
import { uploadFile } from "../../../services/uploads.service";
import {
  CONFIRM_UPLOAD_POPUP_TITLE,
  GENERIC_ERROR_MESSAGE,
  INVESTOR_DELETE_SUCCESS,
  INVESTORS_COLUMN_ORDER_VIEW_KEY,
  TEXT_CANCEL,
  TEXT_CONFIRM,
  TEXT_CONFIRM_DELETE,
  UPLOAD_RESULTS_POPUP_DESCRIPTION,
  UPLOAD_RESULTS_POPUP_TITLE,
} from "../../../utils/constants/text.constants";
import { arrayIndexUpdate as updateArrayIndex } from "../../../utils/helpers/columnOrder.helper";
import { ImageItem } from "../../../utils/types/listItems";
import { ScopeRole } from "../../../utils/types/user.type";
import InvestorAddOptionsPopover from "./InvestorAddOptionsPopover/InvestorAddOptionsPopover";
import InvestorDetailPanel from "./investorDetailPanel/InvestorDetailPanel";
import {
  useBulkActionOptionSelectionEffect,
  useInvestorsEffect,
  useSearchTextEffect,
} from "./InvestorList.hooks";
import { ButtonBox } from "./InvestorList.styles";
import MfaModal from "./MfaModal";

const InvestorList: React.FC = (): ReactElement => {
  const { state, informationAlert } = useContext(AppContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clientId = state.loginUser?.clientId;
  const { hasRole: isSuperAdminOrClientAdmin } = useRole([
    ScopeRole.SUPER_ADMIN,
    ScopeRole.ARK_CLIENT_ADMIN,
    ScopeRole.BASIC_ADMIN,
  ]);
  const {
    headerList,
    activeHeaderFields,
    investorFilteredList,
    originalInvestorList,
    setInvestorsList,
    handleUpdateHeader,
    investorListLoaded,
    sugestionList,
    isLoadingInvestorList,
    handleFilter,
    showActivateConfirmationPopover,
    setShowActivateConfirmationPopover,
    showPanel,
    setShowPanel,
    selectedInvestorId,
    setSelectedInvestorId,
    handleOnView,
    searchText,
    setSearchText,
    investorSelectionModel,
    setInvestorSelectionModel,
    isActivating,
    showDeleteConfirmationPopover,
    setShowDeleteConfirmationPopover,
    handleBulkOptionClick,
    selectedInvestorListItem,
    setSelectedInvestorListItem,
    showMfaModal,
    setShowMfaModal,
    userMfaInfo,
    setUserMfaInfo,
    setLoadingInvestorList,
  } = useInvestorsEffect(isSubmitting, clientId);
  const history = useHistory();

  useSearchTextEffect(searchText, originalInvestorList, setInvestorsList);

  const [showSuggestionPopover, setShowSuggestionPopover] = useState(false);

  const [showAddOptions, setShowAddOptions] = useState(false);
  const [showUploadFileConfirmation, setShowUploadFileConfirmation] =
    useState(false);
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [addOptionsAnchorEl, setAddOptionsAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);
  const [fileUploaded, setFileUpdaloaded] = useState<File | null>();
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const handleOnSearch = (newValue: string | null) => {
    if (typeof newValue === "string") {
      setSearchText(newValue);
    } else if (newValue === null) {
      setSearchText("");
    }
  };

  const handleAddNewButtonAction = (event: any) => {
    setShowAddOptions(true);
    setAddOptionsAnchorEl(event.currentTarget);
  };

  const handleOnAddOptionsClose = (event: any) => {
    setShowAddOptions(false);
  };

  const handleDeleteInvestorsCancel = () => {
    setShowDeleteConfirmationPopover(false);
  };

  const handleOnNewInvestor = (event: any) => {
    setShowAddOptions(false);
    setSelectedInvestorId("new");
    setSelectedInvestorListItem(null);
    setShowPanel(true);
  };

  const handleDeleteInvestorsConfirmation = async () => {
    setIsSubmitting(true);
    try {
      await deleteInvestors(investorSelectionModel);
      informationAlert(INVESTOR_DELETE_SUCCESS, "success");
    } catch (exception) {
      informationAlert(GENERIC_ERROR_MESSAGE, "error");
    } finally {
      setShowDeleteConfirmationPopover(false);
      setIsSubmitting(false);
    }
  };

  const handleActivateInvestorsCancel = () => {
    setShowActivateConfirmationPopover(false);
  };

  const handleActivateInvestorsConfirmation = async () => {
    setIsSubmitting(true);
    try {
      await updateInvestorStatus(
        investorSelectionModel,
        isActivating ? "ACTIVE" : "INACTIVE"
      );
    } catch (exception) {
      informationAlert(GENERIC_ERROR_MESSAGE, "error");
    } finally {
      setShowActivateConfirmationPopover(false);
      setIsSubmitting(false);
    }
  };

  const defaultBulkActions: ImageItem[] = [
    {
      id: "export",
      text: "Export",
      icon: <img src={ExportIcon} alt="" height="15" />,
      optionsSelected: 0,
    },
    {
      id: "activate",
      text: "Activate",
      icon: <img src={GreenEllipse} alt="" width="10" height="10" />,
      optionsSelected: 0,
    },
    {
      id: "inactivate",
      text: "Inactivate",
      icon: <img src={RedEllipse} alt="" width="10" height="10" />,
      optionsSelected: 0,
    },
    {
      id: "delete",
      text: "Delete",
      icon: <img src={DeleteIcon} alt="" height="15" />,
      optionsSelected: 0,
    },
  ];

  const { bulkActionOptions } = useBulkActionOptionSelectionEffect(
    defaultBulkActions,
    investorSelectionModel
  );
  const handleOnTemplateUpload = (event: any) => {
    const fileSelected = event.target.files[0];

    if (fileSelected) {
      setFileUpdaloaded(fileSelected);
      setShowUploadFileConfirmation(true);
    }
  };

  const handleOnTemplateUploadCancel = (event: any) => {
    setShowUploadFileConfirmation(false);
    setShowAddOptions(false);
  };
  const onRowColumnOrderChange = (
    params: GridColumnOrderChangeParams,
    event: MuiEvent<{}>,
    etails: GridCallbackDetails
  ) => {
    const newIndex = params.targetIndex;
    const oldIndex = params.oldIndex;

    const columnOrderToSave = updateArrayIndex(
      headerList,
      oldIndex - 1,
      newIndex - 1,
      clientId,
      INVESTORS_COLUMN_ORDER_VIEW_KEY
    );

    saveColumnOrder(columnOrderToSave);
  };

  const handleOnTemplateUploadConfirmation = async (event: any) => {
    if (!fileUploaded) return;
    try {
      setIsSubmitting(true);
      setIsUploadingFile(true);
      await uploadFile("investors", fileUploaded);
      setIsUploadingFile(false);
      setShowUploadResults(true);
    } catch (exception) {
      informationAlert(GENERIC_ERROR_MESSAGE, "error");
      setShowUploadResults(false);
    } finally {
      setShowUploadFileConfirmation(false);
      setIsSubmitting(false);
    }
  };

  const handleOnTemplateUploadResultCancel = (event: any) => {
    setShowUploadResults(false);
  };

  const handleOnTemplateUploadResult = (event: any) => {
    setShowUploadResults(false);
    history.push(RoutingPaths.UploadHistory);
  };

  return (
    <DataWrapperBox id="investor_list_main" role="main">
      <Progress
        id="investor_detail_list_loading"
        showProgress={isLoadingInvestorList || isUploadingFile}
      />

      <HorizontalBox fullWidth noPadding>
        <HorizontalBox noPadding>
          <Typography variant="h1">Investors</Typography>

          <SearchBar
            id="txt_search_investor"
            size="small"
            onChange={handleOnSearch}
            value={searchText}
          />
        </HorizontalBox>

        <ButtonBox>
          <SplitButton
            id={"btn_bulk_action_options"}
            options={bulkActionOptions}
            hidden={investorSelectionModel.length === 0}
            handleOptionClick={handleBulkOptionClick}
            ariaLabelMessage="Select bulk action option"
          />

          {isSuperAdminOrClientAdmin && (
            <Button
              id="btn_add_new_options_button"
              variant="contained"
              icon={<GridAddIcon />}
              text="Add New"
              color={"primary"}
              onClick={handleAddNewButtonAction}
            />
          )}
        </ButtonBox>
      </HorizontalBox>

      {(!isLoadingInvestorList || investorListLoaded) && investorFilteredList && (
        <DataGrid
          id="investor_data_grid"
          dataList={investorFilteredList}
          headerList={headerList}
          autoHeight={false}
          minHeight="1vh"
          handleOnView={handleOnView}
          selectionModel={investorSelectionModel}
          setSelectionModel={setInvestorSelectionModel}
          activeHeaderFields={activeHeaderFields}
          handleUpdateHeader={handleUpdateHeader}
          handleFilter={handleFilter}
          onColumnOrderChange={onRowColumnOrderChange}
          onNextPage={() => { }}
        />
      )}

      {selectedInvestorId && (
        <InvestorDetailPanel
          showPanel={showPanel}
          setShowPanel={setShowPanel}
          selectedInvestor={selectedInvestorId}
          setSelectedInvestor={setSelectedInvestorId}
          selectedInvestorListItem={selectedInvestorListItem}
          setSelectedInvestorListItem={setSelectedInvestorListItem}
          isSubmittingInvestor={isSubmitting}
          setSubmitingInvestor={setIsSubmitting}
        />
      )}

      <FullScreenModal
        id="dialog_investor_list_activate_confirmation"
        open={showActivateConfirmationPopover}
        title={`Are you sure you want to ${isActivating ? "activate" : "inactivate"
          } (${investorSelectionModel.length}) items?`}
        subtitle={undefined}
        confirmButtonText={TEXT_CONFIRM}
        cancelButtonText={TEXT_CANCEL}
        onSuccess={handleActivateInvestorsConfirmation}
        onCancel={handleActivateInvestorsCancel}
      />

      <InvestorAddOptionsPopover
        id={"pop_investor_list_add_options"}
        anchorEl={addOptionsAnchorEl}
        showPopover={showAddOptions}
        handleOnPopoverClose={handleOnAddOptionsClose}
        handleOnTemplateUpload={handleOnTemplateUpload}
        handleOnNewEntity={handleOnNewInvestor}
      />

      <FullScreenModal
        id="modal_investor_list_upload_confirmation"
        open={showUploadFileConfirmation}
        title={CONFIRM_UPLOAD_POPUP_TITLE}
        subtitle={
          <div>
            Are you sure you want to upload{" "}
            <strong>{fileUploaded?.name}</strong> ?
          </div>
        }
        confirmButtonText="Upload"
        cancelButtonText="Cancel"
        onSuccess={handleOnTemplateUploadConfirmation}
        onCancel={handleOnTemplateUploadCancel}
      />

      <FullScreenModal
        id="modal_investor_list_upload_results"
        open={showUploadResults}
        title={UPLOAD_RESULTS_POPUP_TITLE}
        subtitle={UPLOAD_RESULTS_POPUP_DESCRIPTION}
        confirmButtonText="Upload History"
        cancelButtonText="Close"
        onSuccess={handleOnTemplateUploadResult}
        onCancel={handleOnTemplateUploadResultCancel}
      />
      <MfaModal
        open={showMfaModal}
        handleClose={() => {
          setShowMfaModal(false);
        }}
        userMfaInfo={userMfaInfo}
        setUserMfaInfo={setUserMfaInfo}
        setLoadingInvestorList={setLoadingInvestorList}
        onSuccessCallback={() => {
          setShowMfaModal(false);
          setShowPanel(true);
        }}
        onfailCallback={() => {
          setSelectedInvestorId("");
          setSelectedInvestorListItem(null);
        }}
      />
      <DeleteConfirmationDialog
        id="dialog_investor_list_delete_confirmation"
        open={showDeleteConfirmationPopover}
        primaryButtonAction={handleDeleteInvestorsConfirmation}
        secondaryButtonAction={handleDeleteInvestorsCancel}
      />
    </DataWrapperBox>
  );
};

export default InvestorList;
